import { useTranslation } from '@pasteltech/react-i18n'
import { memo, useCallback, useMemo, useState } from 'react'
import { styled } from 'styled-components'
import { SearchIcon } from '../../svg-components'
import { ScreenQuery } from '../../utilities'
import { Case, CaseCard, CaseDetailOverlay } from '../case'

const Background = styled.div`
  background-image: url('/images/showcase-page-background.png');
  background-position: right top;
  background-repeat: no-repeat;
  background-size: cover;
`
const Container = styled.div`
  padding: ${({ theme }) => theme.sizes.spacings.xxxxl}px 0
    ${({ theme }) => theme.sizes.spacings.xxxxxl}px;
  width: 75%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  ${ScreenQuery.tablet} {
    flex-direction: column;
    width: 80%;
  }
  ${ScreenQuery.mobile} {
    flex-direction: column;
    width: 90%;
  }
  & > * {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    ${ScreenQuery.mobile} {
      grid-template-columns: repeat(1, 1fr);
    }
    ${ScreenQuery.tablet} {
      grid-template-columns: repeat(2, 1fr);
    }
    gap: ${({ theme }) => theme.sizes.spacings.xxxxl}px;
  }
`
const SearchFieldContainer = styled.div`
  width: 100%;
`
const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`
const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  pointer-events: none;
  display: flex;
  align-items: center;
`
const SearchField = styled.input`
  padding: 16px 16px 16px 48px; /* Added left padding for icon space */
  width: 100%;
  border-radius: ${({ theme }) => theme.sizes.borderRadius.xs}px;
  border: 1px solid ${({ theme }) => theme.colors.border.low};
  background: ${({ theme }) => theme.colors.container.lowest};
`
const CardListContainer = styled.div``

type Props = {
  data: Case[]
}
export const CaseListSection = memo(({ data }: Props) => {
  const { t } = useTranslation()
  const [searchText, setSearchText] = useState('')
  const [selectedPost, setSelectedPost] = useState<Case | null>(null)

  const filteredData = useMemo(() => {
    const lowerCasedSearchText = searchText.toLowerCase()
    return data.filter(
      (post) =>
        post.title?.toLowerCase().includes(lowerCasedSearchText) ?? false,
    )
  }, [data, searchText])
  const cards = useMemo(
    () =>
      filteredData.map((post) => ({
        post,
        onClick: () => setSelectedPost(post),
      })),
    [filteredData],
  )
  const handleOverlayClose = useCallback(() => {
    setSelectedPost(null)
  }, [])
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value)
    },
    [],
  )

  return (
    <Background>
      <Container>
        <SearchFieldContainer>
          <SearchInputContainer>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <SearchField
              type="text"
              placeholder={t('common.searchPlaceholder')}
              value={searchText}
              onChange={handleChange}
            />
          </SearchInputContainer>
        </SearchFieldContainer>
        <CardListContainer>
          {cards.map(({ post, onClick }) => (
            <CaseCard key={post.id} post={post} onClick={onClick} />
          ))}
        </CardListContainer>
        {selectedPost && (
          <CaseDetailOverlay onClose={handleOverlayClose} post={selectedPost} />
        )}
      </Container>
    </Background>
  )
})

CaseListSection.displayName = 'CaseListSection'
